import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '@/services/api/apiService';

const PORT = import.meta.env.VITE_COOK_PORT;
const URL = import.meta.env.VITE_API_COOK_URL;

export interface FoodSchedule {
  id: number;
  foodNameItemId: any;
  isActive: boolean;
  createdAt: string;
  mealTypeId: number;
  auditTrailId: number | null;
  foodImageUrl: string;
  mealTypeName: string;
  cookProfileId: number;
  priceOverride: number;
  scheduledDate: string;
  foodMenuItemId: number;
  specialMention: string;
  deliveryEndTime: string;
  deliveryStartTime: string;
  quantityAvailable: number;
  preorderClosingDatetime: string;
  allergens: Allergen[];
}
export interface Allergen {
  allergenName: string;
  allergenTypeId: number;
  examples: string;
}
export interface GetByIdFoodSchedule {
  id: number;
  allergens: Allergen[];
}
interface FoodScheduleState {
  data: FoodSchedule[];
  getByIdFoodSchedules: FoodSchedule[];
  Ingredient_Allergen: [] | any | null;
  loading: boolean;
  error: string | null | undefined;
}

const initialState: FoodScheduleState = {
  data: [],
  Ingredient_Allergen: [],
  getByIdFoodSchedules: [],
  loading: false,
  error: null,
};

interface ApiResponse {
  data: FoodSchedule[];
}

export const fetchFoodSchedules = createAsyncThunk<FoodSchedule[]>(
  'foodSchedule/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiService.post<ApiResponse>('/get_all_food_schedule', {}, PORT, URL);
      return response.data as FoodSchedule[];
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food schedules');
    }
  },
);

export const createFoodSchedules = createAsyncThunk<FoodSchedule[], void, { rejectValue: string }>(
  'foodSchedule/createSchedule',
  async (payload, thunkAPI) => {
    try {
      const response = await apiService.post<ApiResponse>(
        '/create_food_schedule',
        payload,
        PORT,
        URL,
      );
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food schedules');
    }
  },
);
export const createFoodScheduleStatusTracking = createAsyncThunk<
  FoodSchedule[],
  void,
  { rejectValue: string }
>('foodSchedule/createFoodScheduleStatusTracking', async (payload, thunkAPI) => {
  try {
    const response = await apiService.post<ApiResponse>(
      '/create_food_schedule_status_tracking',
      payload,
      PORT,
      URL,
    );
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food schedules');
  }
});

export const customUpdateFoodSchedule = createAsyncThunk<
  FoodSchedule[],
  void,
  { rejectValue: string }
>('foodSchedule/customUpdateFoodSchedule', async (payload, thunkAPI) => {
  try {
    const response = await apiService.put<ApiResponse>(
      '/custom_update_food_schedule',
      payload,
      PORT,
      URL,
    );
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food schedules');
  }
});

export const getByIdFoodSchedule = createAsyncThunk<
  FoodSchedule[],
  { id?: number | string; isForCook?: any },
  { rejectValue: string }
>('foodSchedule/getByIdFoodSchedule', async (params, { rejectWithValue }) => {
  try {
    const config = { params };
    const response = await apiService.get<any>('get_by_id_food_schedule', PORT, URL, config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});

export const getByIdFoodMenuItemIngredient_Allergen = createAsyncThunk<
  any,
  { foodMenuItemId: number | string },
  { rejectValue: string }
>('foodSchedule/getByIdFoodMenuItemIngredient_Allergen', async (params, { rejectWithValue }) => {
  try {
    const config = { params };

    const response = await apiService.get<any>(
      'get_by_id_food_menu_item_ingredient_and_allergen',
      PORT,
      URL,
      config,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});

export const createFoodMenuItemIngredient = createAsyncThunk<
  FoodSchedule[],
  void,
  { rejectValue: string }
>('foodSchedule/createFoodMenuItemIngredient', async (payload, thunkAPI) => {
  try {
    const response = await apiService.post<ApiResponse>(
      '/create_food_menu_item_ingredient',
      payload,
      PORT,
      URL,
    );
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food Menu Item Ingredients');
  }
});
export const createFoodScheduleCancellation = createAsyncThunk<
  FoodSchedule[],
  void,
  { rejectValue: string }
>('foodSchedule/createFoodScheduleCancellation', async (payload, thunkAPI) => {
  try {
    const response = await apiService.post<ApiResponse>(
      '/create_food_schedule_cancellation',
      payload,
      PORT,
      URL,
    );
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch food Menu Item Ingredients');
  }
});

export const foodScheduleSlice = createSlice({
  name: 'foodSchedule',
  initialState,
  reducers: {
    clearFoodScheduleById: (state) => {
      state.getByIdFoodSchedules = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFoodSchedules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFoodSchedules.fulfilled, (state, action: PayloadAction<FoodSchedule[]>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFoodSchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getByIdFoodSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByIdFoodSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.getByIdFoodSchedules = action.payload;
      })
      .addCase(getByIdFoodSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load cities';
      })
      .addCase(createFoodMenuItemIngredient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getByIdFoodMenuItemIngredient_Allergen.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByIdFoodMenuItemIngredient_Allergen.fulfilled, (state, action) => {
        state.loading = false;
        state.Ingredient_Allergen = action.payload;
      })
      .addCase(getByIdFoodMenuItemIngredient_Allergen.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load cities';
      })
      .addCase(createFoodMenuItemIngredient.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(createFoodMenuItemIngredient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFoodScheduleStatusTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFoodScheduleStatusTracking.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(createFoodScheduleStatusTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFoodScheduleCancellation.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(createFoodScheduleCancellation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFoodScheduleCancellation.pending, (state) => {
        state.loading = true;
        state.error = null;
      });
  },
});

export const { clearFoodScheduleById } = foodScheduleSlice.actions;
export default foodScheduleSlice.reducer;
